import type { BoardToolbarModel } from '@/components/WorkItemsKanbanBoard';
import type { AssignedUser } from '@/components/kanban/kanban-shared';

export interface BuildWorkItemsKanbanForwardedPropsInput {
  boardKey?: string;
  listViewBoardKey: string;
  workflowStates: Array<{ key: string; label: string; color: string; description?: string }>;
  resolvedModuleKey: string;
  assignedUsersOverride: AssignedUser[];
  currentUserId?: number;
  workflowField: string;
  workspaceId?: string;
  enableTimeAllocation: boolean;
  stylePreset?: string;
  columnColorOverrides?: Record<string, {
    headerBackground?: string;
    headerTextColor?: string;
    border?: string;
    countBadgeBackground?: string;
  }>;
  preferenceReloadKey: number;
  onColumnsExposed?: (columns: Array<{ key: string; label: string; color: string; description?: string }>) => void;
  onUpdateSchedule?: (itemId: string, scheduledStart: string, scheduledEnd: string, resourceId: number) => void;
  defaultBoardViewMode?: 'status' | 'assignment' | 'time_allocation' | 'resource_schedule';
  hideBoardViewToggle: boolean;
  onAppointmentChange?: (event: { action: 'created' | 'updated' | 'moved'; appointmentId?: string; date: string; assignedTo?: number }) => void;
  onNavigateToRoutes?: (date: string) => void;
  activeViewLabel?: string;
  onOpenBoardPreferences?: () => void;
  onOpenTeamMember?: (userId: number) => void;
  hideBoardToolbar: boolean;
  onToolbarModelChange?: (toolbar: BoardToolbarModel | null) => void;
  /** True when the WorkItemsFilterBar has any active filter (search/status/priority/assignee/tags/date/custom).
   *  Used by the Resource Schedule Timeline to hide resource rows that have no matching items. */
  filtersActive?: boolean;
  /** Non-null user IDs from the active assignee filter. When present, the Resource Schedule Timeline
   *  shows exactly those user rows (not co-assignees of their items) plus team rows with matching items. */
  activeAssigneeUserIds?: number[];
  /** Called when the schedule's selected date changes, so the parent can re-fetch
   *  with a date-windowed API query instead of loading all records. */
  onScheduleDateChange?: (date: Date) => void;
}

export function buildWorkItemsKanbanForwardedProps({
  boardKey,
  listViewBoardKey,
  workflowStates,
  resolvedModuleKey,
  assignedUsersOverride,
  currentUserId,
  workflowField,
  workspaceId,
  enableTimeAllocation,
  stylePreset,
  columnColorOverrides,
  preferenceReloadKey,
  onColumnsExposed,
  onUpdateSchedule,
  defaultBoardViewMode,
  hideBoardViewToggle,
  onAppointmentChange,
  onNavigateToRoutes,
  activeViewLabel,
  onOpenBoardPreferences,
  onOpenTeamMember,
  hideBoardToolbar,
  onToolbarModelChange,
  filtersActive,
  activeAssigneeUserIds,
  onScheduleDateChange,
}: BuildWorkItemsKanbanForwardedPropsInput) {
  return {
    boardKey,
    preferenceScopeKey: listViewBoardKey,
    workflowStates,
    moduleKey: resolvedModuleKey,
    assignedUsersOverride,
    currentUserId,
    workflowField,
    hideColumnSettings: Boolean(workspaceId),
    useStrictWorkflowStates: Boolean(workspaceId) || workflowStates.length > 0,
    enableTimeAllocation,
    stylePreset,
    columnColorOverrides,
    preferenceReloadKey,
    onColumnsExposed,
    onUpdateSchedule,
    defaultBoardViewMode,
    hideBoardViewToggle,
    onAppointmentChange,
    onNavigateToRoutes,
    activeViewLabel,
    onOpenBoardPreferences,
    onOpenTeamMember,
    hideToolbar: hideBoardToolbar,
    onToolbarModelChange: hideBoardToolbar ? onToolbarModelChange : undefined,
    filtersActive,
    activeAssigneeUserIds,
    onScheduleDateChange,
  };
}